/**
 * Shared component-level style utilities.
 *
 * Phase 2 migration: these now use liftango-ui tokens from opsTokens
 * instead of MUI `theme.palette.*`. Function signatures keep an optional
 * `_theme?: any` parameter so existing call-sites that pass `theme` still
 * compile without changes — the parameter is ignored.
 */
import { opsColors, opsElevation, opsRadius } from './opsTokens';

const DRAWER_BUTTON_RADIUS = 8;

/* ------------------------------------------------------------------ */
/*  Shell / Card                                                       */
/* ------------------------------------------------------------------ */

export const shellCardSx = (_theme?: any) => ({
  backgroundColor: opsColors.neutral.panel,
  borderRadius: 3,
  border: `1px solid ${opsColors.neutral.line}`,
  boxShadow: opsElevation.drawer.shadow,
});

export const overlayControlSx = (_theme?: any) => ({
  minWidth: 0,
  p: 0,
  width: 44,
  height: 44,
  borderRadius: '22px 0 0 22px',
  backgroundColor: opsColors.neutral.panel,
  color: opsColors.neutral.ink,
  border: `1px solid ${opsColors.neutral.line}`,
  boxShadow: opsElevation.raised.shadow,
  '&:hover': {
    backgroundColor: opsColors.neutral.panelAlt,
  },
});

/* ------------------------------------------------------------------ */
/*  Pill buttons (standalone, not inside drawers)                      */
/* ------------------------------------------------------------------ */

export const primaryPillButtonSx = (_theme?: any) => ({
  borderRadius: DRAWER_BUTTON_RADIUS,
  textTransform: 'none',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: opsColors.brand.primary,
  border: `1px solid ${opsColors.brand.primaryHover}`,
  '&:hover': {
    backgroundColor: opsColors.brand.primaryHover,
  },
});

export const secondaryPillButtonSx = (_theme?: any) => ({
  borderRadius: DRAWER_BUTTON_RADIUS,
  textTransform: 'none',
  fontWeight: 600,
  color: opsColors.neutral.ink,
  backgroundColor: opsColors.neutral.panel,
  border: `1px solid ${opsColors.neutral.lineStrong}`,
  '&:hover': {
    backgroundColor: opsColors.neutral.panelAlt,
  },
});

export const destructivePillButtonSx = (_theme?: any) => ({
  borderRadius: DRAWER_BUTTON_RADIUS,
  textTransform: 'none',
  fontWeight: 600,
  color: opsColors.semantic.error.color,
  backgroundColor: opsColors.neutral.panel,
  border: `1px solid ${opsColors.semantic.error.soft}`,
  '&:hover': {
    backgroundColor: opsColors.semantic.error.soft,
    borderColor: opsColors.semantic.error.color,
  },
});

/* ------------------------------------------------------------------ */
/*  Drawer action buttons                                              */
/* ------------------------------------------------------------------ */

export const drawerActionButtonSx = {
  minHeight: 36,
  px: 1.75,
  gap: 0.75,
  borderRadius: `${DRAWER_BUTTON_RADIUS}px !important`,
};

export const drawerActionRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 1,
  flexWrap: 'wrap',
};

export const drawerPrimaryActionSx = (_theme?: any) => ({
  ...primaryPillButtonSx(),
  ...drawerActionButtonSx,
});

export const drawerSecondaryActionSx = (_theme?: any) => ({
  ...secondaryPillButtonSx(),
  ...drawerActionButtonSx,
});

export const drawerDestructiveActionSx = (_theme?: any) => ({
  ...destructivePillButtonSx(),
  ...drawerActionButtonSx,
});

export const drawerTertiaryActionSx = (_theme?: any) => ({
  minHeight: 32,
  px: 1.25,
  textTransform: 'none',
  fontWeight: 500,
  color: opsColors.neutral.inkSoft,
  borderRadius: DRAWER_BUTTON_RADIUS,
  '&:hover': {
    backgroundColor: opsColors.neutral.panelAlt,
    color: opsColors.neutral.ink,
  },
});

/* ------------------------------------------------------------------ */
/*  Segmented control / top-nav                                        */
/* ------------------------------------------------------------------ */

export const segmentedControlButtonSx = (_theme: any, selected: boolean) => ({
  borderRadius: DRAWER_BUTTON_RADIUS,
  px: 2,
  py: 0.75,
  minHeight: 36,
  textTransform: 'none',
  fontWeight: selected ? 700 : 500,
  color: selected ? '#ffffff' : opsColors.neutral.ink,
  backgroundColor: selected ? opsColors.brand.primary : opsColors.neutral.panel,
  border: `1px solid ${selected ? opsColors.brand.primary : opsColors.neutral.line}`,
  '&:hover': {
    backgroundColor: selected ? opsColors.brand.primaryHover : opsColors.neutral.panelAlt,
  },
});

export const topNavTextButtonSx = (_theme: any, selected: boolean) => ({
  minWidth: 0,
  height: 40,
  px: 1.75,
  borderRadius: 0,
  textTransform: 'none',
  fontSize: '0.95rem',
  fontWeight: selected ? 600 : 500,
  letterSpacing: '0.1px',
  color: selected ? opsColors.neutral.ink : opsColors.neutral.inkSoft,
  borderBottom: `2px solid ${selected ? opsColors.brand.primary : 'transparent'}`,
  '&:hover': {
    backgroundColor: 'transparent',
    color: opsColors.neutral.ink,
  },
  '&:focus-visible': {
    outline: `2px solid ${opsColors.brand.primary}`,
    outlineOffset: 2,
  },
  transition: 'background-color 0.16s ease, color 0.16s ease, border-color 0.16s ease',
});

/* ------------------------------------------------------------------ */
/*  Drawer surfaces (previously frosted-glass, now opaque)             */
/* ------------------------------------------------------------------ */

export const frostedDrawerPaperSx = (_theme?: any, _alpha?: number) => ({
  backgroundColor: opsColors.neutral.panel,
  border: `1px solid ${opsColors.neutral.line}`,
  boxShadow: opsElevation.drawer.shadow,
});

export const frostedDrawerHeaderSx = (_theme?: any, _alpha?: number) => ({
  backgroundColor: opsColors.neutral.panel,
  borderBottom: '1px solid',
  borderColor: opsColors.neutral.line,
});

export const frostedDrawerCardSx = (_theme?: any, _alpha?: number) => ({
  backgroundColor: opsColors.neutral.panel,
  border: `1px solid ${opsColors.neutral.line}`,
  boxShadow: opsElevation.raised.shadow,
});
